/**
 * FileUploadSection Component
 * Handles dual file upload for predictors and target variable with drag-and-drop
 */
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DragDropFileUpload from '@/features/data-upload/components/DragDropFileUpload';

interface FileUploadSectionProps {
  file1: File | null;
  file2: File | null;
  onFile1Upload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFile2Upload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile1?: () => void;
  onRemoveFile2?: () => void;
  error?: string | null;
}

const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  file1,
  file2,
  onFile1Upload,
  onFile2Upload,
  onRemoveFile1,
  onRemoveFile2
}) => {
  const { t } = useTranslation('dataCloud');
  const fileInput1Ref = useRef<HTMLInputElement>(null!);
  const fileInput2Ref = useRef<HTMLInputElement>(null!);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
      {/* File 1: Predictors */}
      <div role="region" aria-labelledby="data-cloud-predictor-heading">
        <h3 id="data-cloud-predictor-heading" className="text-sm sm:text-base font-semibold mb-2">
          {t('fileInput.predictors')}
        </h3>
        <DragDropFileUpload
          inputId="file-upload-predictors"
          onFileChange={onFile1Upload}
          fileInputRef={fileInput1Ref}
          accept=".csv"
          multiple={false}
          selectedFiles={file1 ? [file1] : []}
          showDropZone={!file1}
          onRemoveFile={() => onRemoveFile1?.()}
        />
      </div>

      {/* File 2: Target Variable */}
      <div role="region" aria-labelledby="data-cloud-target-heading">
        <h3 id="data-cloud-target-heading" className="text-sm sm:text-base font-semibold mb-2">
          {t('fileInput.targetVariable')}
        </h3>
        <DragDropFileUpload
          inputId="file-upload-target"
          onFileChange={onFile2Upload}
          fileInputRef={fileInput2Ref}
          accept=".csv"
          multiple={false}
          selectedFiles={file2 ? [file2] : []}
          showDropZone={!file2}
          onRemoveFile={() => onRemoveFile2?.()}
        />
      </div>
    </div>
  );
};

export default FileUploadSection;
